import React from "react";
import { Form } from "antd";
import {
  Controller,
  Control,
  FieldValues,
  PathValue,
  Path,
} from "react-hook-form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { InfoIconOutlined } from "../../CustomIcons";

interface TextEditorProps<T extends FieldValues> {
  fieldName: Path<T>;
  label?: string;
  control: Control<T>;
  defaultValue?: PathValue<T, Path<T>> | undefined;
  iProps?: React.InputHTMLAttributes<HTMLInputElement>;
  isRequired?: boolean;
  rules?: object;
  validate?: "" | "success" | "warning" | "error" | "validating" | undefined;
  validMessage?: string | React.ReactNode;
  className?: string;
  prefix?: React.ReactNode;
  tooltipTitle?: string | React.ReactNode; // Use ReactNode for tooltip title
  optionalLabel?: string;
  hintMessage?: string;
  warning?: boolean;
}

export const TextEditor = <T extends FieldValues>({
  fieldName,
  isRequired = false, // Provide default value
  label,
  validMessage,
  className,
  control,
  defaultValue, // Provide default value
  rules,
  iProps,
  tooltipTitle,
  optionalLabel,
  hintMessage,
  warning,
  validate,
}: TextEditorProps<T>) => {
  return (
    <Form.Item
      required={isRequired}
      label={<div className="label_text">{label}</div>}
      validateStatus={validate}
      help={validMessage}
      className={className}
      tooltip={
        tooltipTitle && {
          title: tooltipTitle,
          placement: "topLeft",
          arrow: false,
          icon: <InfoIconOutlined />,
        }
      }
    >
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field: { onChange, value } }) => (
          <>
            <ReactQuill
              theme="snow"
              onChange={(description: string) => onChange(description)}
              value={value || ""}
              readOnly={iProps?.disabled}
              modules={{
                toolbar: [
                  [{ header: [1, 2, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ align: [] }],
                  ["link", "image"],
                  ["clean"],
                ],
              }}
              formats={[
                "header",
                "bold",
                "italic",
                "underline",
                "strike",
                "list",
                "bullet",
                "align",
                "link",
                "image",
              ]}
            />
            {optionalLabel && (
              <div className="optional-label">{optionalLabel}</div>
            )}
            {hintMessage && (
              <div
                className={warning ? "hint-label warning-label" : "hint-label"}
              >
                {hintMessage}
              </div>
            )}
          </>
        )}
      />
    </Form.Item>
  );
};
